import {
  BlockhashWithExpiryBlockHeight,
  Commitment,
  ComputeBudgetProgram,
  Connection,
  Keypair,
  PublicKey,
  Signer,
  Transaction,
  TransactionInstruction,
  VersionedTransaction,
} from '@solana/web3.js'
import * as anchor from '@project-serum/anchor'
import { web3 } from '@project-serum/anchor'
// Note: Using namespace import due to multiple @solana/spl-token versions in node_modules
// The actual version is 0.3.7 which has the new function-based API
import * as spl from '@solana/spl-token'
import { Metadata } from '@metaplex/js'

const TOKEN_PROGRAM_ID: PublicKey = spl.TOKEN_PROGRAM_ID
const ASSOCIATED_TOKEN_PROGRAM_ID: PublicKey = spl.ASSOCIATED_TOKEN_PROGRAM_ID
import axios from 'axios'
import config, { connection, getBaseUrl } from '../config/config'
import { NftMetadata } from './nftmetaData.type'
import asyncBatch from 'async-batch'
import { ParsedTokenAccount } from './types'
import * as fs from 'fs'
import reattempt from 'reattempt'
import saveAs from 'file-saver'
import _ from 'lodash'
import assert from 'assert'
import { Constants } from 'discord.js'
import { LegacyNFT } from './useWalletNFTs'

const TOKEN_METADATA_PROGRAM_ID = new anchor.web3.PublicKey(
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s'
)

export async function getOrCreateAssociatedTokenAddressInstruction(
  mint: PublicKey,
  owner: PublicKey,
  connection: anchor.web3.Connection,
  payer?: PublicKey,
  allowOwnerOffCurve?: boolean,
  tokenProgram = TOKEN_PROGRAM_ID
) {
  const address = await getAssociatedTokenAddress(mint, owner, allowOwnerOffCurve ?? true, tokenProgram)

  const tokenAccount = await connection.getAccountInfo(address)

  console.log('token account exists', tokenAccount)

  let instructions: web3.TransactionInstruction[] = []
  if (!tokenAccount) {
    // Use the new API: createAssociatedTokenAccountInstruction(payer, ata, owner, mint)
    instructions.push(
      spl.createAssociatedTokenAccountInstruction(
        payer ?? owner,
        address,
        owner,
        mint,
        tokenProgram,
        ASSOCIATED_TOKEN_PROGRAM_ID
      )
    )
  }

  return {
    address,
    instructions,
  }
}

function estimateTransactionSize(tx: Transaction): number {
  let size = 32; // Base overhead for signatures
  size += tx.signatures.length * 64; // Each signature is 64 bytes
  size += 3; // Message header (3 bytes)

  const uniqueAccounts = new Set<string>();
  for (const instruction of tx.instructions) {
      size += 32; // Program ID (32 bytes)
      size += instruction.keys.length; // Each account index is 1 byte
      size += instruction.data.length; // Actual instruction data size
      instruction.keys.forEach((key) => uniqueAccounts.add(key.pubkey.toBase58()));
  }

  size += uniqueAccounts.size * 32; // Account addresses (each 32 bytes)
  
  return size;
}


export function isTransactionTooLarge(tx: Transaction): {
  isTooLarge: boolean
  size: number
} {
  const size = estimateTransactionSize(tx)

  if (size > 1230) {
    console.log('tx is too large', size)
    return {
      isTooLarge: true,
      size,
    }
  }

  return {
    isTooLarge: false,
    size,
  }
}

export function increaseComputePriceInstruction(price?: number) {
  return ComputeBudgetProgram.setComputeUnitPrice({
    microLamports: price ?? 50_001,
  })
}

export function increaseComputeUnitInstruction(units?: number) {
  return ComputeBudgetProgram.setComputeUnitLimit({
    units: units ?? 300000,
  })
}

export async function getAssociatedTokenAddress(
  mint: PublicKey,
  owner: PublicKey,
  allowOwnerOffCurve = false,
  tokenProgram = TOKEN_PROGRAM_ID
): Promise<PublicKey> {
  // Use the new spl-token API (0.3.x+)
  return spl.getAssociatedTokenAddress(
    mint,
    owner,
    allowOwnerOffCurve,
    tokenProgram,
    ASSOCIATED_TOKEN_PROGRAM_ID
  )
}

export async function getTokenAccount(
  connection: Connection,
  mint: PublicKey,
  user: PublicKey,
  includeEmpty?: boolean,
) {
  const userTokenAccounts = await connection.getParsedTokenAccountsByOwner(
    user,
    {
      mint: mint,
    }
  )

  if (userTokenAccounts.value.length === 0) return null
  return (userTokenAccounts.value.find(
    (t) => includeEmpty || t.account.data.parsed.info.tokenAmount.uiAmount
  ) ?? userTokenAccounts.value[0]) as ParsedTokenAccount
}

export async function getTokenAccountAdressOrCreateTokenAccountInstruction({
  connection,
  mint,
  user,
  payer,
}: {
  connection: Connection
  mint: PublicKey
  user: PublicKey
  payer?: PublicKey
}) {
  const userTokenAccount = await getTokenAccount(connection, mint, user)

  if (userTokenAccount)
    return {
      address: userTokenAccount.pubkey,
      instructions: [],
    }

  return await getOrCreateAssociatedTokenAddressInstruction(
    mint,
    user,
    connection,
    payer
  )
}

export async function getNftWithMetadata({
  mint,
  _metadata,
  noProxy,
}: {
  mint: PublicKey
  _metadata?: Metadata
  noProxy?: boolean
}) {
  const chainMetadata =
    _metadata ??
    (await (async () => {
      let [pda] = await anchor.web3.PublicKey.findProgramAddress(
        [
          Buffer.from('metadata'),
          TOKEN_METADATA_PROGRAM_ID.toBuffer(),
          new anchor.web3.PublicKey(mint).toBuffer(),
        ],
        TOKEN_METADATA_PROGRAM_ID
      )

      const accountInfo: any = await connection.getParsedAccountInfo(pda)

      return new Metadata(mint.toString(), accountInfo.value)
    })())

  const baseUrl = getBaseUrl()

  const metadataRes = !noProxy
    ? await axios.get(baseUrl + '/api/prox', {
        params: {
          uri: chainMetadata.data.data.uri,
        },
      })
    : await axios.get(chainMetadata.data.data.uri)

  /*  let uri = chainMetadata.data.data.uri.replace(
    'https://ipfs.io',
    'https://cloudflare-ipfs.com'
  )

  const metadataRes = await axios.get(uri) */

  return { ...chainMetadata, ...metadataRes.data } as NftMetadata
}

export async function getNftWithOnChainMetadata(mint: PublicKey) {
  try {
    const pda = await Metadata.getPDA(mint)
    const chainMetadata = await Metadata.load(connection, pda)
    return chainMetadata
  } catch (e) {
    console.error('error in getting nft with on chain metadata', e)
    throw new Error('error in getting nft with on chain metadata')
  }
}

/* export async function getAllNftsWithMetadataByOwner(connection: Connection, owner: PublicKey) {
  const metaplex = new Metaplex(connection)
  const nftsOrMetadatas = await metaplex.nfts().findAllByOwner({owner})
  const allNfts = await Promise.all(nftsOrMetadatas.map(async metadata => {
    if (metadata.jsonLoaded) return metadata
    const nft = await metaplex.nfts().load({ metadata: metadata as any, loadJsonMetadata: true });
    return nft
  }))
  return allNfts.filter(nft => !!nft.name)
} */

export async function getNftFromMetadata(metadata: {data: {mint: string, data: {uri: string}}}) {
  try {
    const dataRes = false
      ? await axios.get(getBaseUrl() + '/api/prox', {
          params: {
            uri: metadata.data.data.uri,
          },
        })
      : await axios.get(metadata.data.data.uri)

    if (dataRes.status !== 200) return null
    return {
      ...dataRes.data,
      ...metadata,
      pubkey: new PublicKey(metadata.data.mint),
    } as NftMetadata
  } catch (e) {
    console.error('error in fetching Meta', e)
    return null
  }
}

export async function getNftWithMetadataNew(mint: PublicKey) {
  const chainMetadata = await Metadata.load(
    connection,
    await Metadata.getPDA(mint)
  )

  const metadataRes = await axios.get(chainMetadata.data.data.uri)

  return { ...chainMetadata, ...metadataRes.data } as NftMetadata
}

export function downloadURI(uri: any, name: any) {
  try {
    saveAs(uri, name)
  } catch (err) {
    window.open(uri, '_blank')
  }
}

export function isValidPubKey(it?: string): boolean {
  try {
    return !!it && PublicKey.isOnCurve(new PublicKey(it))
  } catch {
    return false
  }
}

export function handleTransactionError(e: any) {
  console.log('Error in transaction', e, e.msg, e.code, e.logs)

  const logLineWithAnchorError: string | undefined =
    e.logs && e.logs.find((log: string) => log.includes('AnchorError'))

  console.log({ logLineWithAnchorError })

  if (logLineWithAnchorError) {
    const logLineSplitted = logLineWithAnchorError.split('Error Message: ')
    const message = logLineSplitted[logLineSplitted.length - 1]
    if (message) {
      console.log('got anchor error message: ', message)
      return message.trim()
    }
  }
}

export function splitAndCapitalize(str: string): string {
  const parts: string[] = str.split('_');
  const capitalizedParts: string[] = parts.map((part: string) => {
    return part.charAt(0).toUpperCase() + part.slice(1);
  });
  return capitalizedParts.join(' ');
}

export async function handleTransaction(
  tx: string,
  opts: {
    showLogs: boolean
    commitment: Commitment
    blockhash?: BlockhashWithExpiryBlockHeight
    connection?: Connection
  } = {
    showLogs: false,
    commitment: 'confirmed',
  }
) {
  await reattempt.run({ times: 3, delay: 1000 }, async () => {
    if (opts.blockhash) {
      const res = await (opts.connection ?? connection).confirmTransaction({
        signature: tx,
        ...opts.blockhash,
      })
      console.log('confirm tx, err', res.value.err?.toString())
      return res
    } else {
      const res = await (opts.connection ?? connection).confirmTransaction(tx)
      console.log('confirm tx, err', res.value.err?.toString())
      return res
    }
  })

  const trans = await (opts.connection ?? connection).getTransaction(tx)

  if (!trans) {
    console.log('transaction not found', tx)

    return tx
  }

  if (opts?.showLogs) {
    console.log('trans logs', trans?.meta?.logMessages)
  }

  console.log('confirmed tx', tx)

  return tx
}

/*
export async function getNftMintInfosForOwnerBatched({
  connection,
  ownerAddress,
}: {
  connection: anchor.web3.Connection
  ownerAddress: anchor.web3.PublicKey
}) {
  console.log({ owner: ownerAddress.toBase58() })

  let tokenAccounts = (
    await connection.getParsedTokenAccountsByOwner(ownerAddress, {
      programId: TOKEN_PROGRAM_ID,
    })
  ).value

  tokenAccounts = tokenAccounts.filter((tokenAccount) => {
    const tokenAmount = tokenAccount.account.data.parsed.info.tokenAmount

    return tokenAmount.amount == '1' && tokenAmount.decimals == '0'
  })

  const tokenAccountBatches = _.chunk(tokenAccounts, 25)

  const mintInfos: {nftMint: string, nftMetadataJson: {name: string, symbol: string, uri: string}, nftCollectionMint: string, nftVerifiedCreatorsArray: string}[] = []

  await asyncBatch(
    tokenAccountBatches,
    async (tokenAccounts) => {
      const mints = tokenAccounts.map((tokenAccount) => {
        return tokenAccount.account.data.parsed.info.mint
      })

      const newMintInfos = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URI}/nft/mint_information`,
        {
          nftMint: mints,
        },
        {
          headers: {
            Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_TOKEN}`,
          },
        }
      )

      mintInfos.push(...newMintInfos.data.data)
    },
    2
  )

  return mintInfos.filter((m) => !!m)
} */

export async function getNftMetadatasForOwnerBatched({
  connection,
  ownerAddress,
}: {
  connection: anchor.web3.Connection
  ownerAddress: anchor.web3.PublicKey
}) {
  console.log({ owner: ownerAddress.toBase58() })

  let tokenAccounts = (
    await connection.getParsedTokenAccountsByOwner(ownerAddress, {
      programId: TOKEN_PROGRAM_ID,
    })
  ).value

  tokenAccounts = tokenAccounts.filter((tokenAccount) => {
    const tokenAmount = tokenAccount.account.data.parsed.info.tokenAmount

    return tokenAmount.amount == '1' && tokenAmount.decimals == '0'
  })

  const tokenAccountBatches = _.chunk(tokenAccounts, 100)

  let metadatas: (anchor.web3.ParsedAccountData | Buffer)[] = []
  await asyncBatch(
    tokenAccountBatches,
    async (tokenAccounts) => {
      const metas = await connection.getMultipleParsedAccounts(
        await Promise.all(
          tokenAccounts.map(async (tokenAccount) => {
            const pda = await Metadata.getPDA(
              new anchor.web3.PublicKey(
                tokenAccount.account.data.parsed.info.mint
              )
            )
            return pda
          })
        )
      )
      if (metas.value) metadatas.push(...(metas.value as any))
    },
    2
  )

  const allMetadatas = (
    await asyncBatch(
      metadatas,
      async (metadata, index, workerIndex) => {
        const accountInfo: any = metadata as any
        /* await connection.getParsedAccountInfo(pda) */

        try {
          const metadata = new Metadata(ownerAddress.toString(), accountInfo)
          return metadata
        } catch (e) {
          console.log('error in getting metadata', e)
          return null
        }
      },
      2
    )
  ).filter((m) => !!m) as Metadata[]

  return allMetadatas
}

export async function getNFTsForOwnerBatched({
  connection,
  ownerAddress,
}: {
  connection: anchor.web3.Connection
  ownerAddress: anchor.web3.PublicKey
}) {
  let tokenAccounts = (
    await connection.getParsedTokenAccountsByOwner(ownerAddress, {
      programId: TOKEN_PROGRAM_ID,
    })
  ).value

  tokenAccounts = tokenAccounts.filter((tokenAccount) => {
    const tokenAmount = tokenAccount.account.data.parsed.info.tokenAmount

    return tokenAmount.amount == '1' && tokenAmount.decimals == '0'
  })

  const tokenAccountBatches = _.chunk(tokenAccounts, 100)

  let metadatas: (anchor.web3.ParsedAccountData | Buffer)[] = []
  await asyncBatch(
    tokenAccountBatches,
    async (tokenAccounts) => {
      const metas = await connection.getMultipleParsedAccounts(
        await Promise.all(
          tokenAccounts.map(async (tokenAccount) => {
            const pda = await Metadata.getPDA(
              new anchor.web3.PublicKey(
                tokenAccount.account.data.parsed.info.mint
              )
            )
            return pda
          })
        )
      )
      console.log(metas.value[0]?.data)
      if (metas.value) metadatas.push(...(metas.value as any))
    },
    2
  )

  const nfts = (
    await asyncBatch(
      metadatas,
      async (metadata, index, workerIndex) => {
        /*  let [pda] = await anchor.web3.PublicKey.findProgramAddress(
          [
            Buffer.from('metadata'),
            TOKEN_METADATA_PROGRAM_ID.toBuffer(),
            new anchor.web3.PublicKey(
              tokenAccount.account.data.parsed.info.mint
            ).toBuffer(),
          ],
          TOKEN_METADATA_PROGRAM_ID
        ) */

        const accountInfo: any = metadata as any
        /* await connection.getParsedAccountInfo(pda) */

        try {
          const metadata = new Metadata(ownerAddress.toString(), accountInfo)

          const dataRes = false
            ? await axios.get(getBaseUrl() + '/api/prox', {
                params: {
                  uri: metadata.data.data.uri,
                },
              })
            : await axios.get(metadata.data.data.uri)

          if (dataRes.status !== 200) return false
          return {
            ...dataRes.data,
            ...metadata,
            pubkey: new PublicKey(metadata.data.mint),
          } as NftMetadata
        } catch (e) {
          console.error('error in fetching Meta', e)
          return null
        }
      },
      2
    )
  ).filter((n) => !!n) as NftMetadata[]

  return nfts
}
/* export async function getNFTsForOwnerV2({
  connection,
  ownerAddress
}: {
  connection: anchor.web3.Connection
  ownerAddress: anchor.web3.PublicKey
}) {
  try {
    const nfts = await getAllNftsWithMetadataByOwner(connection, ownerAddress)
    
    return nfts
  } catch (e) {
    console.log('error in new method', e)
  }
} */

export async function getNFTsForOwner({
  connection,
  ownerAddress,
}: {
  connection: anchor.web3.Connection
  ownerAddress: anchor.web3.PublicKey
}) {
  let tokenAccounts = (
    await connection.getParsedTokenAccountsByOwner(ownerAddress, {
      programId: TOKEN_PROGRAM_ID,
    })
  ).value

  tokenAccounts = tokenAccounts.filter((tokenAccount) => {
    const tokenAmount = tokenAccount.account.data.parsed.info.tokenAmount

    console.log(
      'tokenaccount',
      tokenAccount.account.data,
      tokenAccount.account.data.parsed
    )

    return tokenAmount.amount == '1' && tokenAmount.decimals == '0'
  })

  return (
    await asyncBatch(
      tokenAccounts,
      async (tokenAccount, index, workerIndex) => {
        return await getNftWithMetadataNew(
          new anchor.web3.PublicKey(tokenAccount.account.data.parsed.info.mint)
        )
      },
      3
    )
  ).filter((n) => !!n)
}

export async function getMetadata(nft: PublicKey) {
  return await Metadata.load(connection, await Metadata.getPDA(nft))
}

export function pub(pubkey: string) {
  return new PublicKey(pubkey)
}

export function getSolAdressFromText(tweet: string) {
  const adresses = tweet.match(/(\b[a-zA-Z0-9]{32,44}\b)/g)
  return adresses && adresses?.length > 0 ? adresses[0] : null
}

export function loadWallet(data: string) {
  return Keypair.fromSecretKey(Uint8Array.from(JSON.parse(data)))
}

export function loadWalletFromFile(path: string) {
  if (!fs.existsSync(path)) throw new Error(`file ${path} does not exist`)
  return loadWallet(fs.readFileSync(path, 'utf-8'))
}

export function isTransactionEqualToSessionData({
  transaction,
  sessionDataTxCheck
}: {
  transaction: Transaction
  sessionDataTxCheck: string
}) {
  if (sessionDataTxCheck != JSON.stringify(transaction.compileMessage())) {
    const originalTxData: ReturnType<typeof transaction.compileMessage> =
      JSON.parse(sessionDataTxCheck)
    const newTxData = transaction.compileMessage()

    const accountKeysAreEqual = originalTxData.accountKeys.every((accountKey) => {
      return newTxData.accountKeys.some(
        (newAccountKey) =>
          accountKey.toString() === newAccountKey.toString()
      )
    })

    const instructionsAreEqual = originalTxData.instructions.every(
      (originalInstruction) => {
        return newTxData.instructions.some(
          (newInstruction) =>
            originalInstruction.data === newInstruction.data
        )
      }
    )

    if (!instructionsAreEqual) {
      console.log('WARN: instructions are not equal')

      const matchingInstructions = newTxData.instructions.filter(
        (instruction) => {
          return originalTxData.instructions.some(
            (originalInstruction) =>
              instruction.data === originalInstruction.data
          )
        }
      )

      if (matchingInstructions.length < 1) {
        throw new Error('Wrong transaction passed as input: instructions')
      } else {
        console.log('WARN: instructions are not equal but at least one is')
      }
    }

    const blockhashIsEqual =
      newTxData.recentBlockhash === originalTxData.recentBlockhash

    if (!accountKeysAreEqual) {
      console.warn('accounts not equal', {
        accountKeysAreEqual,
        instructionsAreEqual,
        blockhashIsEqual,
      })
      throw new Error('Wrong transaction passed as input: accounts')
    }

    if (!blockhashIsEqual) {
      console.warn('blockhash not equal', {
        accountKeysAreEqual,
        instructionsAreEqual,
        blockhashIsEqual,
      })
    }
  }

  return true
}

export async function sendTransactionWithRetry(
  serializedTransaction: Buffer,
) {
  const txSig = await reattempt.run(
    { times: 3, delay: 600 },
    async () => {
      return await connection.sendRawTransaction(serializedTransaction, {
        preflightCommitment: 'confirmed',
        skipPreflight: true
      })
    }
  )
  return txSig
}

export async function sendTransaction(args: {
  instructions: TransactionInstruction[]
  signers: Signer[]
  feePayer?: PublicKey
  log?: boolean
}) {
  const blockHash = await getLatestBlockhash()
  const transaction = new Transaction({
    recentBlockhash: blockHash.blockhash,
    feePayer: args.feePayer,
  }).add(
    increaseComputePriceInstruction(),
    increaseComputeUnitInstruction(599999)
  ).add(...args.instructions)

  await transaction.sign(...args.signers)

  const tx = await connection.sendRawTransaction(
    transaction.serialize({ verifySignatures: false }),
    {skipPreflight: true}
  )
  if (args.log) console.log(`sent transaction ${tx}`)

  return tx
}

export async function getLatestBlockhash() {
  // return connection.getRecentBlockhash()
  return await ((connection as any).getLatestBlockhashAndContext
    ? connection.getLatestBlockhash()
    : connection.getRecentBlockhash())
}

export async function sendAndConfirmTransaction(args: {
  instructions: TransactionInstruction[]
  signers: Signer[]
  feePayer?: PublicKey
  commitment?: Commitment
  log?: boolean
}) {
  const { commitment = 'confirmed', log = false } = args
  const tx = await sendTransaction(args)

  await reattempt.run({ times: 3 }, async () => {
    await connection.confirmTransaction(tx, commitment)
  })

  if (log) console.log(`confirmed transaction ${tx}`)

  return tx
}

export function getSolscanTxLink(tx: string) {
  return `https://solscan.io/tx/${tx}?cluster=${config.solanaEnv}`
}

export const sendAndHandleTx = async (
  conn: Connection,
  tx: Transaction | VersionedTransaction,
  blockhashData: Awaited<ReturnType<Connection['getLatestBlockhash']>>,
  printTxId: boolean
) => {
  const serializedTx = tx.serialize()

  const sig = await reattempt.run({ times: 3, delay: 1500 }, async () => {
    return conn.sendRawTransaction(serializedTx, {
      skipPreflight: true,
    })
  })

  const res = await conn.confirmTransaction(
    {
      signature: sig,
      blockhash: blockhashData.blockhash,
      lastValidBlockHeight: blockhashData.lastValidBlockHeight,
    },
    'processed'
  )

  return {confirmedSig: sig, res}
}
